import React, { createContext, useContext, useState, useEffect } from 'react';
import sharedDevRoutes from '../configs/shared/devRoutes.json';
import { deepMerge } from '../utils/deepMerge';
import { getSubdomainOverride } from '../configs/subdomainConfig';

interface UniversityConfig {
  universityId: string;
  name: string;
  [key: string]: any;
}

interface UniversityConfigContextType {
  config: UniversityConfig | null;
  isLoading: boolean;
  error: string | null;
}

const UniversityConfigContext = createContext<UniversityConfigContextType>({
  config: null,
  isLoading: true,
  error: null
});

export const useUniversityConfig = () => {
  const context = useContext(UniversityConfigContext);
  if (!context) {
    throw new Error('useUniversityConfig must be used within UniversityConfigProvider');
  }
  return context;
};

interface UniversityConfigProviderProps {
  children: React.ReactNode;
}

const mergeSharedDevRoutes = (config: UniversityConfig): UniversityConfig => {
  const mergedConfig = { ...config };

  if (!mergedConfig.navigation) {
    mergedConfig.navigation = {};
  }

  mergedConfig.navigation.devRoutes = sharedDevRoutes;

  return mergedConfig;
};

const applySubdomainOverrides = (config: UniversityConfig): UniversityConfig => {
  const subdomainOverride = getSubdomainOverride();

  if (!subdomainOverride) return config;

  return deepMerge(config, subdomainOverride);
};

const loadUniversityConfig = async (universityId: string): Promise<UniversityConfig> => {
  try {
    let universityConfig: UniversityConfig;

    try {
      const module = await import(`../configs/universities/${universityId}/index.ts`);
      universityConfig = {
        universityId,
        name: module.default.name || `University ${universityId}`,
        ...module.default
      };
    } catch (indexError) {
      const module = await import(`../configs/universities/${universityId}.json`);
      universityConfig = {
        universityId,
        name: module.default.name || `University ${universityId}`,
        ...module.default
      };
    }

    const configWithSharedDocs = mergeSharedDevRoutes(universityConfig);
    const configWithSubdomainOverrides = applySubdomainOverrides(configWithSharedDocs);

    return configWithSubdomainOverrides;
  } catch (error) {
    throw new Error(`Failed to load configuration for university: ${universityId}`);
  }
};

export const UniversityConfigProvider: React.FC<UniversityConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<UniversityConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const activeUniversity = import.meta.env.VITE_ACTIVE_UNIVERSITY;

        const universityConfig = await loadUniversityConfig(activeUniversity);

        // Hide navigation only when the LMS is embedded inside an iframe (demo mode).
        // Direct users on the subdomain get the full navigation experience.
        const isEmbedded = window.self !== window.top;
        if (isEmbedded) universityConfig.hideNavigation = true;

        setConfig(universityConfig);

        if (universityConfig.title) {
          document.title = universityConfig.title;
        } else if (universityConfig.name) {
          document.title = universityConfig.name;
        }

        if (universityConfig.theme?.favicon) {
          const link = (document.querySelector("link[rel*='icon']") as HTMLLinkElement) || document.createElement('link');
          link.type = 'image/x-icon';
          link.rel = 'shortcut icon';
          link.href = universityConfig.theme.favicon;
          document.getElementsByTagName('head')[0].appendChild(link);
        }

        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown configuration error');
        setIsLoading(false);
      }
    };

    initializeConfig();
  }, []);

  const contextValue: UniversityConfigContextType = {
    config,
    isLoading,
    error
  };

  return <UniversityConfigContext.Provider value={contextValue}>{children}</UniversityConfigContext.Provider>;
};

export default UniversityConfigProvider;
